<template>
  <define-form>
    <pf-empty-state v-if="loading">
      <pf-empty-state-header :title="loadingTitle" heading-level="h4">
        <template #icon>
          <pf-empty-state-icon>
            <pf-spinner />
          </pf-empty-state-icon>
        </template>
      </pf-empty-state-header>
    </pf-empty-state>

    <pf-form
      v-else
      v-bind="{ ...ouiaProps, ...$attrs }"
      :id="formId"
      :ref="(v: any) => (formRef = v)"
      :component="fake ? 'div' : 'form'"
      :method="method"
      :action="action"
      novalidate
      :horizontal="horizontal === true"
      :class="{
        [`pf-m-horizontal-${horizontal}`]: typeof horizontal === 'string',
      }"
      @submit="submit"
    >
      <fieldset ref="fieldset" :class="styles.form" :disabled="disabled || sending ? true : undefined">
        <slot />
      </fieldset>

      <div v-if="!fake && (submitErrors.length || submitText || $slots.submit || $slots.actions)" :class="[styles.formGroup, styles.modifiers.action]">
        <pf-alert-group>
          <pf-alert
            v-for="(error, i) in submitErrors"
            :key="i"
            :variant="[500, 502].includes(error.status) || Number(error.data?.result) === 1 ? 'danger' : 'warning'"
            inline
            :title="$t('Errore di salvataggio')"
            @close="submitErrors.splice(i, 1)"
          >
            <template v-if="error.data?.message">
              {{ error.data.message }}
            </template>
            <template v-else-if="error.status === 400">
              {{ $t('Richiesta malformata.') }}
            </template>
            <template v-else-if="error.status === 401">
              {{ $t('Sessione scaduta.') }}
            </template>
            <template v-else-if="error.status === 413">
              {{ $t('La dimensione dei dati inviati supera il limite consentito dal server.') }}
            </template>
            <template v-else-if="error.status === 409">
              {{ $t('Il record che si sta inserendo risulta già presente.') }}
            </template>
            <template v-else-if="error.status === 503">
              {{ $t('Il server sembra essere in manutenzione. Riprovare più tardi.') }}
            </template>
            <template v-else>
              {{ $t('Si è verificato un errore inatteso.') }}
            </template>
          </pf-alert>
        </pf-alert-group>

        <div v-if="!modal && (submitText || $slots.submit || $slots.actions)" :class="styles.formGroupControl">
          <div :class="styles.formActions">
            <pf-button
              v-if="submitText || $slots.submit"
              type="submit"
              variant="primary"
              :aria-disabled="!valid"
              :inoperable-events="[]"
              :disabled="toValue(effectiveDisabled) || preventSubmit"
              :loading="sending"
              :large="submitBlock"
              :block="submitBlock"
              @click="validate"
            >
              <slot name="submit">{{ submitText }}</slot>
            </pf-button>
            {{ ' ' }}
            <slot name="actions" />
            {{ ' ' }}
            <em v-if="submitHelp">{{ submitHelp }}</em>
          </div>
        </div>
      </div>
    </pf-form>
  </define-form>

  <pf-modal
    v-if="modal"
    :open="open"
    :no-close="noClose"
    :no-body-wrapper="noBodyWrapper"
    :disable-focus-trap="disableFocusTrap"
    :element-to-focus="elementToFocus"
    :title="title"
    :title-label="titleLabel"
    :title-icon-variant="titleIconVariant"
    :append-to="appendTo"
    :aria-describedby="ariaDescribedby"
    :variant="variant"
    :width="width"
    :max-width="maxWidth"
    :position="position"
    :position-offset="positionOffset"
    :descriptor-id="descriptorId"
    :component="component"
    @update:open="$event ? $emit('update:open', $event) : cancelClick()"
  >
    <template #description>
      <slot name="description" />
    </template>

    <reuse-form />

    <template #footer>
      <slot name="footer" :form-id="formId">
        <pf-button
          v-if="submitText || $slots.submit"
          type="submit"
          variant="primary"
          :form="formId"
          :aria-disabled="!valid"
          :inoperable-events="[]"
          :disabled="toValue(effectiveDisabled) || preventSubmit"
          :loading="sending"
          :large="submitBlock"
          :block="submitBlock"
          @click="validate"
        >
          <slot name="submit">{{ submitText }}</slot>
        </pf-button>
        <pf-button v-if="!noClose" variant="link" @click="cancelClick">
          {{ cancelText }}
        </pf-button>
        <em v-if="submitHelp">{{ submitHelp }}</em>
        <slot name="actions" />
      </slot>
    </template>
  </pf-modal>

  <reuse-form v-else />
</template>

<style lang="scss" scoped>
em {
  display: inline-flex;
  align-items: center;
}

fieldset {
  border: none;
}

.pf-v6-c-form__group.pf-m-action {
  bottom: calc(var(--pf-v6-c-drawer--child--PaddingBottom) * -1);
  position: sticky;
  overflow: initial;
  background-color: var(--pf-v6-c-drawer__panel--BackgroundColor);
  padding-bottom: var(--pf-v6-c-drawer--child--PaddingBottom);
  padding-left: var(--pf-v6-c-drawer--child--PaddingLeft);
  margin-left: calc(var(--pf-v6-c-drawer--child--PaddingLeft) * -1);
  padding-right: var(--pf-v6-c-drawer--child--PaddingRight);
  margin-right: calc(var(--pf-v6-c-drawer--child--PaddingRight) * -1);

  > :deep(.pf-v6-c-alert-group) {
    grid-column: 1 / 3;
  }
}

:deep(.pf-v6-c-alert.pf-m-inline:not(:last-child)) {
  margin-bottom: 20px;
}

/*
// intranet/node_modules/.pnpm/@patternfly+patternfly@5.2.1/node_modules/@patternfly/patternfly/components/Form/form.scss
*/

:deep(.pf-v6-c-form__group) {
  grid-template-columns: var(--pf-v6-c-form--m-horizontal__group-label--md--GridColumnWidth) var(
      --pf-v6-c-form--m-horizontal__group-control--md--GridColumnWidth
    );
  grid-column-gap: var(--pf-v6-c-form--m-horizontal__group-label--md--GridColumnGap);
}

:deep(.pf-v6-c-form__group-control) {
  grid-column: 2;
}

@mixin form-horizontal-responsive($suffix, $width) {
  @container form (min-width: #{$width}) {
    .pf-v6-c-form.pf-m-horizontal-#{$suffix} {
      --pf-v6-c-form__group-label--PaddingBottom: 0;

      :deep(.pf-v6-c-form__group) {
        display: grid;
      }

      :deep(.pf-v6-c-form__group-label) {
        padding-block-start: var(--pf-v6-c-form--m-horizontal__group-label--md--PaddingTop);

        &.pf-m-no-padding-top {
          --pf-v6-c-form--m-horizontal__group-label--md--PaddingTop: var(--pf-v6-c-form--m-horizontal__group-label--m-no-padding--md--PaddingTop);
          transform: translateY(var(--pf-v6-c-form--m-horizontal__group-label--m-no-padding--md--TranslateY));
        }
      }

      &.pf-m-align-right {
        :deep(.pf-v6-c-form__label) {
          text-align: end;
        }
      }
    }
  }
}

@include form-horizontal-responsive(sm, 450px);
@include form-horizontal-responsive(md, 650px);
@include form-horizontal-responsive(lg, 900px);
@include form-horizontal-responsive(xl, 1200px);
</style>

<script lang="ts">
export const FormIdInjectionKey = Symbol('X-Form ID') as InjectionKey<string>;
</script>

<script lang="ts" setup>
import { computed, provide, toValue, useId, type InjectionKey } from 'vue';
import { useXForm } from '@common/form';
import styles from '@patternfly/react-styles/css/components/Form/form';
import type { Method } from 'axios';
import { $t as _t } from '@/i18n';
import type PfForm from '@vue-patternfly/core/components/Form/Form.vue';
import type PfModal from '@vue-patternfly/core/components/Modal/Modal.vue';
import type { ComponentProps } from 'vue-component-type-helpers';
import { createReusableTemplate } from '@vueuse/core';
import { useOUIAProps } from '@common/ouia';

type PfFormProps = ComponentProps<typeof PfForm>;
type PfModalProps = ComponentProps<typeof PfModal>;

interface Props extends /* @vue-ignore */ Omit<PfFormProps, 'horizontal' | 'novalidate' | 'component' | 'onSubmit' | 'maxWidth'> {
  id?: string;
  action?: string;
  submitText?: string;
  submitHelp?: string;
  submitBlock?: boolean;
  cancelText?: string;
  hidden?: boolean;
  disabled?: boolean;
  encoding?: 'form' | 'json';
  fake?: boolean;
  horizontal?: boolean | 'sm' | 'md' | 'lg' | 'xl';
  method?: Method;
  xhr?: boolean;
  modal?: boolean;
  preventSubmit?: boolean;
  sending?: boolean;
  loading?: boolean;
  loadingTitle?: string;
  ouiaId?: string;
  ouiaSafe?: boolean;
  additionalData?: Record<string, any> | (() => Record<string, any>);
  onBeforeSubmit?: () => boolean | undefined | void;
  onSubmit?: (e: Event | undefined, responseData?: any) => MaybePromise<void>;
  onCancel?: () => void;
  onCheckValidity?: (valid: boolean) => void;

  open?: PfModalProps['open'];
  noClose?: PfModalProps['noClose'];
  noBodyWrapper?: PfModalProps['noBodyWrapper'];
  disableFocusTrap?: PfModalProps['disableFocusTrap'];
  elementToFocus?: PfModalProps['elementToFocus'];
  title?: PfModalProps['title'];
  titleLabel?: PfModalProps['titleLabel'];
  titleIconVariant?: PfModalProps['titleIconVariant'];
  appendTo?: PfModalProps['appendTo'];
  ariaDescribedby?: PfModalProps['ariaDescribedby'];
  variant?: PfModalProps['variant'];
  width?: PfModalProps['width'];
  maxWidth?: PfModalProps['maxWidth'];
  position?: PfModalProps['position'];
  positionOffset?: PfModalProps['positionOffset'];
  descriptorId?: PfModalProps['descriptorId'];
  component?: PfModalProps['component'];
}

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<Props>(), {
  submitText: _t('Procedi'),
  cancelText: _t('Annulla'),
  loadingTitle: _t('Caricamento in corso. Attendere...'),
  encoding: 'form',
  horizontal: 'md',
  method: 'post',
  sending: undefined,
  additionalData: () => ({}),
});

defineSlots<{
  default: (props?: Record<never, never>) => any;
  submit: (props?: Record<never, never>) => any;
  actions: (props?: Record<never, never>) => any;
  description: (props?: Record<never, never>) => any;
  footer: (props?: { formId: string }) => any;
}>();

const emit = defineEmits<{
  (name: 'update:sending', value: boolean): void;
  (e: 'update:open', value: boolean): void;
  (name: 'cancel'): void;
  (name: 'close'): void;
}>();

const [DefineForm, ReuseForm] = createReusableTemplate();
const ouiaProps = useOUIAProps(props, {
  safe: computed(() => !props.loading && props.ouiaSafe),
});
const formId = props.id ?? useId();

provide(FormIdInjectionKey, formId);

const { form, formRef, fieldset, submit, submitErrors, sending, valid, validate, checkValidity, effectiveDisabled } = useXForm({
  method: () => props.method,
  disabled: () => props.disabled || props.loading,
  xhr: () => props.xhr,
  preventSubmit: () => props.preventSubmit,
  action: () => props.action,
  encoding: () => props.encoding,
  sending: () => props.sending,
  additionalData: props.additionalData,
  onBeforeSubmit: props.onBeforeSubmit,
  onSubmit: props.onSubmit,
  onCheckValidity: props.onCheckValidity,
});

function cancelClick() {
  emit('cancel');
  emit('update:open', false);
  emit('close');
}

defineExpose({
  form,
  fieldset,
  checkValidity,
});
</script>
